// Temporary icon library used by the Question authoring flow + the
// QuestionOverlay's `iconKey` rendering path.
//
// This is a STUB. The long-term plan is for the video-generator API to
// return the entities present in each generated cartoon (e.g.
// `{ video: '…', entities: [{ id, label, imageUrl, ts }] }`), and the
// authoring UI will let the parent pick from those video-specific images
// when building a question. That way the answer tile is literally
// recognizable from the scene the kid just watched.
//
// Until then this 20-image set is enough to wire the pipeline end-to-end:
// Studio picker → save `iconKey` on the option → renderer hits this map.
//
// URLs are pinned to Twemoji's jsDelivr CDN (free, no auth, stable, kid
// readable). When you swap in the real video-extraction API, replace this
// map's contents — every consumer reads from `STUB_ICONS[iconKey]` so
// nothing else changes.

export type StubIcon = {
  label: string;
  imageUrl: string;
};

// 72×72 PNGs from twemoji via jsDelivr (CC-BY-4.0).
const CDN = "https://cdn.jsdelivr.net/gh/jdecked/twemoji@15.1.0/assets/72x72";

export const STUB_ICONS: Record<string, StubIcon> = {
  // Animals
  "animals/dog":   { label: "Dog",     imageUrl: `${CDN}/1f436.png` },
  "animals/cat":   { label: "Cat",     imageUrl: `${CDN}/1f431.png` },
  "animals/cow":   { label: "Cow",     imageUrl: `${CDN}/1f42e.png` },
  "animals/pig":   { label: "Pig",     imageUrl: `${CDN}/1f437.png` },
  "animals/frog":  { label: "Frog",    imageUrl: `${CDN}/1f438.png` },
  "animals/bird":  { label: "Bird",    imageUrl: `${CDN}/1f426.png` },
  "animals/fish":  { label: "Fish",    imageUrl: `${CDN}/1f41f.png` },
  "animals/bear":  { label: "Bear",    imageUrl: `${CDN}/1f43b.png` },

  // Food
  "food/apple":      { label: "Apple",     imageUrl: `${CDN}/1f34e.png` },
  "food/banana":     { label: "Banana",    imageUrl: `${CDN}/1f34c.png` },
  "food/cookie":     { label: "Cookie",    imageUrl: `${CDN}/1f36a.png` },
  "food/cake":       { label: "Cake",      imageUrl: `${CDN}/1f370.png` },
  "food/ice-cream":  { label: "Ice cream", imageUrl: `${CDN}/1f366.png` },

  // Nature
  "nature/sun":    { label: "Sun",    imageUrl: `${CDN}/2600.png`  },
  "nature/moon":   { label: "Moon",   imageUrl: `${CDN}/1f319.png` },
  "nature/star":   { label: "Star",   imageUrl: `${CDN}/2b50.png`  },
  "nature/tree":   { label: "Tree",   imageUrl: `${CDN}/1f333.png` },
  "nature/flower": { label: "Flower", imageUrl: `${CDN}/1f338.png` },

  // Transport
  "transport/car":   { label: "Car",   imageUrl: `${CDN}/1f697.png` },
  "transport/plane": { label: "Plane", imageUrl: `${CDN}/2708.png`  },
};

// Helpful for the Studio's picker grid — preserves the curated order
// rather than relying on object-key iteration.
export const STUB_ICON_KEYS: ReadonlyArray<string> = Object.keys(STUB_ICONS);

// Categories for future filtering in the picker UI.
export const STUB_ICON_CATEGORIES: ReadonlyArray<{
  key: string;
  label: string;
  iconKeys: string[];
}> = [
  {
    key: "animals",
    label: "Animals",
    iconKeys: STUB_ICON_KEYS.filter((k) => k.startsWith("animals/")),
  },
  {
    key: "food",
    label: "Food",
    iconKeys: STUB_ICON_KEYS.filter((k) => k.startsWith("food/")),
  },
  {
    key: "nature",
    label: "Nature",
    iconKeys: STUB_ICON_KEYS.filter((k) => k.startsWith("nature/")),
  },
  {
    key: "transport",
    label: "Transport",
    iconKeys: STUB_ICON_KEYS.filter((k) => k.startsWith("transport/")),
  },
];

// Convenience lookup. Returns undefined for unknown keys so the renderer
// can fall back to the auto-detection path (count / color / letter / text).
export function stubIcon(iconKey: string | undefined): StubIcon | undefined {
  if (!iconKey) return undefined;
  return STUB_ICONS[iconKey];
}
